import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { dateFormat, showError, showSuccess } from '../utils/helper';
import global from '../global';

import beepSound from '../assets/sound/beep-29.mp3';
import beepButton from '../assets/sound/button-21.mp3';

import Layout from '../Components/Layout';
import LeftSidebar from '../Components/LeftSidebar';

import SelectPosModal from '../Components/SelectPosModal';
import TransfersAlert from '../Components/TransfersAlert';
import {installLogSender} from '../utils/logSender';

const appVersion = process.env.REACT_APP_POS_VERSION;
const versionCheckInterval = process.env.REACT_APP_POS_VERSION_CHECK_INTERVAL;

const App = (props) => {
  const navigate = useNavigate();

  const [newVersion, setNewVersion] = useState('');
  const [lastChecked, setLastChecked] = useState(null);

  const location = useLocation();
  const { hash, pathname } = location;
  const [invoiceDevices, setInvoiceDevices] = useState({});
  const [showSelectPosModal, setShowSelectPosModal] = useState(true);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [checkRequired, setCheckRequired] = useState(false);
  const [shiftChecked, setShiftChecked] = useState(false);

  useEffect(() => {
    const invoiceDeviceTmp = JSON.parse(localStorage.getItem('invoiceDevice'));

    if (invoiceDeviceTmp != null && invoiceDeviceTmp.id) {
      global.invoiceDevice = invoiceDeviceTmp;
      setShowSelectPosModal(false);

      // Osvježi invoiceDevice iz backenda (npr. da se pokupi has_card_processor i druge promjene)
      window.axios.get('/invoice-devices')
        .then((response) => {
          const devices = response?.data?.data
          if (!Array.isArray(devices)) return
          const fresh = devices.find((d) => d.id === invoiceDeviceTmp.id)
          if (fresh) {
            global.invoiceDevice = fresh
            localStorage.setItem('invoiceDevice', JSON.stringify(fresh))
          }
        })
        .catch(() => {
          // Tiho — ako se ne uspije osvježiti, nastavljamo s cached verzijom
        })
    } else {
      setShowSelectPosModal(true);
    }

    // Pokreni slanje logova: prvi pokušaj 30s nakon boota, zatim svakih 60 min.
    installLogSender();
  }, []);

  const fetchRemoteVersion = useCallback(
    () =>
      fetch(`/version.json?${Date.now()}`, {
        cache: 'no-store',
      })
        .then((res) => {
          if (!res.ok) {
            console.warn('Version fetch failed with status:', res.status);
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (!data || !data.version) {
            console.warn('Invalid version data:', data);
            return null;
          }
          setNewVersion(data.version);
          setLastChecked(new Date());
          return data.version;
        })
        .catch((error) => {
          console.warn('Version check error:', error);
          return null;
        }),
    []
  );

  useEffect(() => {
    // Initial version check
    fetchRemoteVersion().then((remoteVersion) => {
      if (!remoteVersion) return;

      if (remoteVersion !== appVersion) {
        console.log('New version available - Local:', appVersion, 'Remote:', remoteVersion);
        setShowNewVersion(true);
        showSuccess('Dostupna je nova verzija aplikacije! Osvježite stranicu za ažuriranje.');
      }
    });

    // Periodic version check
    const interval = parseInt(versionCheckInterval) || 300000;

    const timer = setInterval(() => {
      fetchRemoteVersion().then((remoteVersion) => {
        if (!remoteVersion) return;

        if (remoteVersion !== appVersion) {
          setShowNewVersion(true);
          showSuccess('Dostupna je nova verzija aplikacije! Osvježite stranicu za ažuriranje.');
        }
      });
    }, interval);

    return () => clearInterval(timer);
  }, [fetchRemoteVersion]);

  const showInvoiceDeviceSelect = () => {
    setShowSelectPosModal(true);
  };


  // Shift check - inicijalna provjera + periodična svakih 5 minuta
  useEffect(() => {
    const SHIFT_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minuta

    const checkShiftStatus = async (isInitial = false) => {
      try {
        const { data } = await window.axios.get('/shift-status');
        if (data.needs_confirmation) {
          navigate('/shift-confirm');
          return;
        }
        if (data.is_blocked) {
          navigate('/shift-block');
          return;
        }
        // Sve OK - dopusti renderanje
        if (isInitial) {
          setShiftChecked(true);
        }
      } catch (e) {
        console.warn('Shift status check failed:', e?.response?.data || e.message);
        // Kod greške propusti (fail-open) da ne blokira rad
        if (isInitial) {
          setShiftChecked(true);
        }
      }
    };

    // Inicijalna provjera odmah
    checkShiftStatus(true);

    // Periodična provjera
    const timer = setInterval(() => checkShiftStatus(false), SHIFT_CHECK_INTERVAL);
    return () => clearInterval(timer);
  }, [navigate]);

  useEffect(() => {
    const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minuta

    const canCheckDevice =
      Array.isArray(global?.invoiceDevice?.permissions) &&
      global.invoiceDevice.permissions.includes('device.check');

    const doStatusCheck = async () => {
      try {
        const deviceId = global?.invoiceDevice?.id;
        if (!deviceId || !canCheckDevice) return;

        if (showSelectPosModal) return;

        const { data } = await window.axios.get('/invoice-device-checks/status', {
          params: { invoice_device_id: deviceId },
        });

        if (data?.needs_check === true) {
          setCheckRequired(true);
          if (location.pathname !== '/check') {
            navigate('/check');
            showSuccess('Potrebno je napraviti provjeru blagajne!');
          }
        } else {
          setCheckRequired(false);
        }
      } catch (e) {

        console.warn('Device check status failed:', e?.response?.data || e.message);
      }
    };

    // inicijalni poziv
    doStatusCheck();
    // interval - kraci kad je provjera potrebna da korisnik ne moze dugo raditi bez provjere
    const intervalMs = checkRequired ? 15 * 1000 : CHECK_INTERVAL_MS;
    const t = setInterval(doStatusCheck, intervalMs);
    return () => clearInterval(t);
  }, [location.pathname, showSelectPosModal, navigate, checkRequired]);

  // Ne renderaj dok se ne provjeri shift status
  if (!shiftChecked) {
    return (
      <div className="flex items-center justify-center h-screen w-full bg-gray-100">
        <div className="text-gray-500 text-lg">Učitavanje...</div>
      </div>
    );
  }

  return (
    <>
      <Layout>
        <LeftSidebar checkRequired={checkRequired} />

        <div className="flex flex-col w-full">
          {/* Nalozi za slanje: traka i skočni prozor (pošiljke za zaprimiti, nalozi za poslati, razlog kašnjenja) */}
          {!showSelectPosModal && <TransfersAlert />}
          <Outlet
            context={{ appVersion, newVersion, versionCheckInterval, lastChecked, showNewVersion, showSelectPosModal, checkRequired, onCheckCompleted: () => setCheckRequired(false) }}
          />
        </div>
      </Layout>

      <SelectPosModal
        showSelectPosModal={showSelectPosModal}
        setShowSelectPosModal={setShowSelectPosModal}
      />
    </>
  );
};

export default App;
